import type {
  AudioAssetRefDict,
  BasicAssetMetadata,
  ImageAssetRefDict,
  VideoAssetRefDict,
} from '../../../../generated/nodeDataTypes'

export type UploadedAssetRef = AudioAssetRefDict | ImageAssetRefDict | VideoAssetRefDict

/** Extra metadata measured at upload time: the image aspect ratio (snapped
 *  server-side to a canonical AspectRatio bucket) and its exact pixel size.
 *  Persisting the ratio into out_image.metadata lets the Director match an
 *  uploaded reference's orientation via inspect_node instead of guessing the
 *  project default. Persisting the exact size lets the canvas reserve the
 *  node's real box before the bitmap decodes (see reservedMediaBox.ts), so a
 *  group of portrait uploads does not re-layout every time a pan remounts it. */
export interface UploadAssetMeta {
  aspectRatio?: string | null
  width?: number | null
  height?: number | null
  /** Video container duration measured server-side at upload. Persisted so
   *  duration-gated actions (AI Rough Cut's 60s limit) can decide before any
   *  job is created. */
  durationSeconds?: number | null
}

function isPositiveInteger(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0
}

export function createUploadAssetRef(path: string, meta?: UploadAssetMeta): UploadedAssetRef {
  const metadata: BasicAssetMetadata = { source: 'upload' }
  if (meta?.aspectRatio) {
    metadata.aspect_ratio = meta.aspectRatio
  }
  const durationSeconds = meta?.durationSeconds
  if (typeof durationSeconds === 'number' && Number.isFinite(durationSeconds) && durationSeconds > 0) {
    metadata.duration_seconds = durationSeconds
  }
  if (isPositiveInteger(meta?.width) && isPositiveInteger(meta?.height)) {
    metadata.actual_width = meta.width
    metadata.actual_height = meta.height
  }
  return {
    history: [
      {
        user_prompt: '',
        result: {
          id: path.split('/').pop() || path,
          local_path: path,
          metadata,
        },
        refs: [],
      },
    ],
    picked_index: 0,
  }
}
